<template>
  <Teleport to="body">
    <Transition name="modal" @after-leave="$emit('close')">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-[10002] overflow-hidden"
        @click.self="close"
      >
        <!-- Backdrop -->
        <div
          class="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity"
          @click="close"
        ></div>

        <!-- Modal -->
        <div class="flex min-h-full items-center justify-center p-4">
          <div
            class="relative w-full max-w-[95vw] bg-gray-900 rounded-2xl shadow-2xl transform transition-all"
            @click.stop
          >
            <!-- Header -->
            <div
              class="flex items-center justify-between px-6 py-4 border-b border-gray-700"
            >
              <div>
                <h3 class="text-xl font-semibold text-white font-['Outfit']">
                  {{ title }}
                </h3>
              </div>

              <!-- Custom Flipbook Controls -->
              <div class="flex items-center gap-2">
                <!-- Page Navigation -->
                <button
                  @click.stop="prevPage"
                  type="button"
                  class="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  title="Page précédente"
                >
                  <Icon
                    name="heroicons:chevron-left"
                    class="w-5 h-5 text-gray-300"
                  />
                </button>

                <!-- Page Counter -->
                <span
                  class="text-sm font-medium text-gray-300 min-w-[40px] sm:min-w-[80px] text-center"
                >
                  {{ currentPage }} / {{ totalPages }}
                </span>

                <button
                  @click.stop="nextPage"
                  type="button"
                  class="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  title="Page suivante"
                >
                  <Icon
                    name="heroicons:chevron-right"
                    class="w-5 h-5 text-gray-300"
                  />
                </button>

                <!-- Divider -->
                <div class="w-px h-6 bg-gray-600 mx-1 hidden sm:block"></div>

                <!-- Zoom Controls -->
                <button
                  @click.stop="zoomOut"
                  type="button"
                  class="p-2 hover:bg-gray-700 rounded-lg transition-colors hidden sm:inline-flex"
                  title="Zoom arrière"
                >
                  <Icon
                    name="heroicons:magnifying-glass-minus"
                    class="w-5 h-5 text-gray-300"
                  />
                </button>

                <button
                  @click.stop="zoomIn"
                  type="button"
                  class="p-2 hover:bg-gray-700 rounded-lg transition-colors hidden sm:inline-flex"
                  title="Zoom avant"
                >
                  <Icon
                    name="heroicons:magnifying-glass-plus"
                    class="w-5 h-5 text-gray-300"
                  />
                </button>

                <!-- Divider -->
                <div class="w-px h-6 bg-gray-600 mx-1 hidden sm:block"></div>

                <!-- Fullscreen Toggle -->
                <button
                  @click.stop="toggleFullscreen"
                  type="button"
                  class="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  title="Plein écran"
                >
                  <Icon
                    name="heroicons:arrows-pointing-out"
                    class="w-5 h-5 text-gray-300"
                  />
                </button>

                <!-- Divider -->
                <div class="w-px h-6 bg-gray-600 mx-1"></div>

                <!-- Close Button -->
                <button
                  @click.stop="close"
                  type="button"
                  class="p-2 hover:bg-gray-700 rounded-lg transition-colors"
                  title="Fermer"
                >
                  <Icon name="heroicons:x-mark" class="w-6 h-6 text-gray-400" />
                </button>
              </div>
            </div>

            <!-- FlipBook Container -->
            <div
              class="relative bg-gray-800 overflow-hidden flex items-center justify-center"
              style="height: 80vh"
            >
              <div
                v-if="loading"
                class="absolute inset-0 flex items-center justify-center z-10 bg-gray-900/50"
              >
                <div class="text-center">
                  <div
                    class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"
                  ></div>
                  <p class="mt-4 text-white">Chargement de la brochure...</p>
                </div>
              </div>

              <!-- jQuery 3D Flipbook Container -->
              <div
                ref="flipbookContainer"
                class="flipbook-container"
                :class="{ 'opacity-0': loading }"
              ></div>
            </div>

            <!-- Simplified Controls - Download Only -->
            <div
              class="flex items-center justify-between px-6 py-4 border-t border-gray-700 bg-gray-900 rounded-b-2xl"
            >
              <div class="text-sm text-gray-400">
                Utilisez les contrôles intégrés du livre pour naviguer
              </div>

              <!-- Download Button -->
              <a
                :href="pdfUrl"
                download
                class="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors"
              >
                <Icon name="heroicons:arrow-down-tray" class="w-5 h-5" />
                Télécharger
              </a>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick, onUnmounted } from "vue";
import { use3DFlipbook } from "~/composables/use3DFlipbook";
import { logger } from "~/utils/logger";

interface Props {
  isOpen: boolean;
  type: "omra" | "ramadhan";
}

const props = defineProps<Props>();
const emit = defineEmits<{
  close: [];
}>();

const isDev = process.env.NODE_ENV === "development";
const loading = ref(true);
const flipbookContainer = ref<HTMLElement | null>(null);
const currentPage = ref(1);
const totalPages = ref(0);

let flipbookInstance: ReturnType<typeof use3DFlipbook> | null = null;

const title = computed(() => {
  return props.type === "ramadhan"
    ? "Brochure Omra Ramadhan 2026"
    : "Brochure Omra";
});

const pdfUrl = computed(() => {
  return props.type === "ramadhan"
    ? "/brochures/omra-ramadhan-2026.pdf"
    : "/brochures/omra-2025-2026.pdf";
});

watch(
  () => props.isOpen,
  async (newValue) => {
    if (newValue) {
      loading.value = true;

      await nextTick();
      // Wait a bit more for the DOM to be fully ready
      setTimeout(() => {
        initFlipbook();
      }, 300);
    } else {
      destroyFlipbook();
    }
  },
);

function initFlipbook() {
  if (!flipbookContainer.value) {
    if (isDev) logger.error("Flipbook container not found");
    loading.value = false;
    return;
  }

  try {
    if (isDev) logger.log("Initializing 3D flipbook");

    flipbookInstance = use3DFlipbook({
      container: flipbookContainer.value,
      pdfUrl: pdfUrl.value,
      onPageChange: (page: number) => {
        currentPage.value = page;
      },
    });

    flipbookInstance
      .init()
      .then(() => {
        setTimeout(() => {
          loading.value = false;
          if (flipbookInstance) {
            totalPages.value = flipbookInstance.getTotalPages();
            currentPage.value = flipbookInstance.getCurrentPage();

            // Hide toolbar in iframe by injecting CSS
            const iframe = flipbookContainer.value?.querySelector("iframe");
            if (iframe) {
              const iframeDoc =
                iframe.contentDocument || iframe.contentWindow?.document;
              if (iframeDoc) {
                const style = iframeDoc.createElement("style");
                style.textContent = `
                  .controls, .fnavbar, .fnav {
                    display: none !important;
                    visibility: hidden !important;
                    height: 0 !important;
                    overflow: hidden !important;
                  }
                `;
                iframeDoc.head.appendChild(style);
              }
            }
          }
        }, 1000);
      })
      .catch((error) => {
        if (isDev) logger.error("Failed to initialize flipbook", error);
        loading.value = false;
      });
  } catch (error) {
    if (isDev) logger.error("Failed to initialize flipbook", error);
    loading.value = false;
  }
}

function destroyFlipbook() {
  if (flipbookInstance) {
    try {
      flipbookInstance.destroy();
      flipbookInstance = null;
    } catch (error) {
      if (isDev) logger.error("Error destroying flipbook:", error);
    }
  }
}

function close() {
  emit("close");
}

function prevPage() {
  if (isDev) logger.log("prevPage clicked", flipbookInstance);
  if (flipbookInstance) {
    flipbookInstance.prevPage();
    setTimeout(() => {
      currentPage.value = flipbookInstance!.getCurrentPage();
    }, 100);
  } else {
    if (isDev) logger.warn("Flipbook instance not initialized");
  }
}

function nextPage() {
  if (isDev) logger.log("nextPage clicked", flipbookInstance);
  if (flipbookInstance) {
    flipbookInstance.nextPage();
    setTimeout(() => {
      currentPage.value = flipbookInstance!.getCurrentPage();
    }, 100);
  } else {
    if (isDev) logger.warn("Flipbook instance not initialized");
  }
}

function zoomIn() {
  if (isDev) logger.log("zoomIn clicked", flipbookInstance);
  if (flipbookInstance) {
    flipbookInstance.zoomIn();
  } else {
    if (isDev) logger.warn("Flipbook instance not initialized");
  }
}

function zoomOut() {
  if (isDev) logger.log("zoomOut clicked", flipbookInstance);
  if (flipbookInstance) {
    flipbookInstance.zoomOut();
  } else {
    if (isDev) logger.warn("Flipbook instance not initialized");
  }
}

function toggleFullscreen() {
  if (isDev) logger.log("toggleFullscreen clicked", flipbookInstance);
  if (flipbookInstance) {
    flipbookInstance.toggleFullscreen();
  } else {
    if (isDev) logger.warn("Flipbook instance not initialized");
  }
}

onUnmounted(() => {
  destroyFlipbook();
});
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .relative,
.modal-leave-active .relative {
  transition: transform 0.3s ease;
}

.modal-enter-from .relative,
.modal-leave-to .relative {
  transform: scale(0.95);
}

.flipbook-container {
  width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
}

/* 3D Flipbook overrides */
:deep(.fb3d-book) {
  margin: 0 auto !important;
  max-width: 100% !important;
  max-height: 100% !important;
}

:deep(.fullscreen) {
  background-color: #1f2937 !important;
}

:deep(.fb3d) {
  background: transparent !important;
}

/* Hide number input arrows */
input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type="number"] {
  -moz-appearance: textfield;
}

/* Hide default 3D Flipbook toolbar */
:deep(.fb3d-controls),
:deep(.controls),
:deep(.fnavbar),
:deep(.fnav),
:deep(.fb3d-menu),
:deep(.fb3d-side-panel) {
  display: none !important;
  visibility: hidden !important;
  opacity: 0 !important;
  pointer-events: none !important;
  height: 0 !important;
  overflow: hidden !important;
}

/* Hide toolbar in iframe content */
:deep(iframe) {
  pointer-events: auto;
}

:deep(iframe .controls),
:deep(iframe .fnavbar) {
  display: none !important;
}

/* Ensure custom buttons are clickable */
button {
  cursor: pointer;
  pointer-events: auto;
}

button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}
</style>
